import { jsx } from 'react/jsx-runtime';
import { useRef, useMemo, useEffect } from 'react';
import { OktaAuth } from '@okta/okta-auth-js';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SCOPES, CLI_SCOPES, AuthenticationContext } from './AuthenticationContext.js';
import { gql } from 'urql';
import { useQuery as useQuery$1 } from '../graphql/graphql.js';
import '@sentry/browser';
import '../logs/chronosphere-logs.js';
import { NotAuthenticatedError } from '../errors/NotAuthenticatedError.js';
import '../app-config/app-config.js';
import '../app-config/boot-flags.js';
import '../app-config/build-data.js';
import { GetCurrentUserContext } from '../generated/graphql.js';

function getRedirectUri(pkceEnabled, authRoutes) {
    return pkceEnabled ? `${window.location.origin}${authRoutes.LOGIN_PKCE_CALLBACK}` : `${window.location.origin}${authRoutes.LOGIN_CALLBACK}`;
}
function getRedirectPath(authRoutes) {
    return `${authRoutes.LOGIN_CALLBACK}`;
}
function getRedirectPkcePath(authRoutes) {
    return `${authRoutes.LOGIN_PKCE_CALLBACK}`;
}
function getImpersonateRedirectUri(pkceEnabled, authRoutes) {
    return pkceEnabled ? `${window.location.origin}${authRoutes.IMPERSONATE_PKCE_CALLBACK}` : `${window.location.origin}${authRoutes.IMPERSONATE_CALLBACK}`;
}
const POST_LOGIN_REDIRECT_URL_PARAM = 'redirectTo';
function getLoginPageLocation(loginRoute) {
    const current = globalThis.location;
    // We only need the relative URL for redirecting after login
    const redirectTo = current.pathname + current.search + current.hash;
    const params = new URLSearchParams();
    params.set(POST_LOGIN_REDIRECT_URL_PARAM, redirectTo);
    return {
        pathname: loginRoute,
        search: `?${params.toString()}`
    };
}
/**
 * GraphQL query used to load the current user.
 */ const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      isAdmin
    }
  }
`;
// Number of times to retry loading auth configuration before erroring
const CONFIG_RETRY_ATTEMPTS = 3;
/**
 * Provides app-level authentication information via context.
 */ function AuthenticationContextProvider(props) {
    const { children, fetchFn, loginRoute, authRoutes } = props;
    const configRequest = useQuery({
        queryKey: [
            'authConfigQuery'
        ],
        queryFn: async ()=>{
            const response = await fetchFn('/auth/config');
            return await response.json();
        },
        retry: CONFIG_RETRY_ATTEMPTS
    });
    const impersonateConfigRequest = useQuery({
        queryKey: [
            'authImpersonateConfigQuery'
        ],
        queryFn: async ()=>{
            const response = await fetchFn('/auth/impersonate/config');
            return await response.json();
        },
        retry: CONFIG_RETRY_ATTEMPTS
    });
    // Recalculate configState whenever the config from the server changes
    const configState = useRef({});
    const impersonateConfigState = useRef({});
    if (configState.current.config !== configRequest.data) {
        const config = configRequest.data;
        // Otherwise, create appropriate state
        let oktaClient = undefined;
        let oktaCliClient = undefined;
        if (config?.type === 'okta') {
            oktaClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: getRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
                scopes: SCOPES,
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: config?.enable_pkce_auth_flow,
                tokenManager: {
                    // TODO: Can we use this rather than hitting our server?
                    autoRenew: false
                }
            });
            oktaCliClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: `${window.location.origin}${authRoutes.CLI_LOGIN_CALLBACK}`,
                scopes: CLI_SCOPES,
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: false,
                tokenManager: {
                    // TODO: Can we use this rather than hitting our server?
                    autoRenew: false
                }
            });
        }
        configState.current = {
            config,
            oktaClient,
            oktaCliClient
        };
    }
    if (!impersonateConfigRequest.error && impersonateConfigState.current.config !== impersonateConfigRequest.data) {
        const config = impersonateConfigRequest.data;
        // Otherwise, create appropriate state
        let oktaClient = undefined;
        let oktaCliClient = undefined;
        if (config?.type === 'okta') {
            oktaClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: getImpersonateRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
                // TODO: Redirect to an admin specific logout callback
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                // TODO: This will force the older 'implicit' flow, we should figure out how to migrate to PKCE
                pkce: config?.enable_pkce_auth_flow,
                tokenManager: {
                    // TODO: Can we use this rather than hitting our server?
                    autoRenew: false
                }
            });
            oktaCliClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: `${window.location.origin}${authRoutes.CLI_IMPERSONATE_CALLBACK}`,
                // TODO: Redirect to an admin specific logout callback
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: false,
                tokenManager: {
                    // TODO: Can we use this rather than hitting our server?
                    autoRenew: false
                }
            });
        }
        impersonateConfigState.current = {
            config,
            oktaClient,
            oktaCliClient
        };
    }
    // Load the currently logged in user from the GraphQL API
    const [{ fetching: loading, error, data }, refreshCurrentUser] = useQuery$1({
        query: GET_CURRENT_USER,
        context: GetCurrentUserContext,
        requestPolicy: 'network-only'
    });
    let currentUser = undefined;
    // NotAuthenticatedErrors are fine and can be ignored since we might not be logged in yet,
    // otherwise we want to throw
    if (error !== undefined && error.networkError instanceof NotAuthenticatedError === false) {
        throw error;
    }
    currentUser = data?.me;
    const authContext = useMemo(()=>({
            isLoading: loading || configRequest.isFetching || impersonateConfigRequest.isFetching,
            config: configState.current.config,
            oktaClient: configState.current.oktaClient,
            oktaImpersonateClient: impersonateConfigState.current.oktaClient,
            oktaCliClient: configState.current.oktaCliClient,
            oktaCliImpersonateClient: impersonateConfigState.current.oktaCliClient,
            oktaImpersonateConfig: impersonateConfigState.current.config,
            pkceEnabled: impersonateConfigState.current.config?.enable_pkce_auth_flow || configState.current.config?.enable_pkce_auth_flow,
            currentUser,
            refreshCurrentUser
        }), [
        loading,
        configRequest,
        impersonateConfigRequest,
        currentUser,
        refreshCurrentUser
    ]);
    const navigate = useNavigate();
    /**
   * Refreshes the user's token on a 10 minute interval
   * This ensures that long running tabs stay valid
   * We also use this to verify that the user's session is still valid
   * Because it's safer to check here than for every request
   */ useEffect(()=>{
        const TEN_MINUTES_MS = 10 * 60 * 1000;
        async function refreshToken() {
            try {
                await fetchFn('/auth/refresh', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        redirect_path: authContext.config?.enable_pkce_auth_flow || authContext.oktaImpersonateConfig?.enable_pkce_auth_flow ? getRedirectPkcePath(authRoutes) : getRedirectPath(authRoutes)
                    })
                });
            } catch (error) {
                /**
         * Redirect on any error to be defensive of API changes
         * But if that presents issues, build in Retry logic to account for transient errors
         * If user's token has expired, take them back to Login
         */ navigate(getLoginPageLocation(loginRoute));
            }
        }
        let refreshInterval;
        // Don't start refreshing the token until we have logged in
        if (authContext.currentUser !== undefined) {
            refreshInterval = setInterval(async ()=>await refreshToken(), TEN_MINUTES_MS);
        }
        return ()=>clearInterval(refreshInterval);
    }, [
        authContext.currentUser,
        authContext.config,
        authContext.oktaImpersonateConfig,
        navigate,
        fetchFn,
        authRoutes,
        loginRoute
    ]);
    return /*#__PURE__*/ jsx(AuthenticationContext.Provider, {
        value: authContext,
        children: children
    });
}

export { AuthenticationContextProvider, GET_CURRENT_USER, POST_LOGIN_REDIRECT_URL_PARAM, getLoginPageLocation };
//# sourceMappingURL=AuthenticationContextProvider.js.map
